import {
  DbSchema,
  OptionalWarehouseIdSchema,
  paramByIDSchema,
  ReadManySchema,
  TypeCustom,
} from '@diamond/shared/schema';
import { Nullable, RecursiveStatic } from '@diamond/shared/schema';
import { MultipartFile, SavedMultipartFile } from '@fastify/multipart';
import { Type } from '@sinclair/typebox';
const B = DbSchema['banner']['properties'];

const BannerBusinessType = Type.Object({
  cg_3_business_type: Type.String(),
  cg_3_business_type_desc: Type.String(),
});

const OptionalOverrides = Type.Partial(
  Type.Pick(DbSchema['banner'], ['banner_type'])
);

const PersonalizedExtras = Type.Object({
  shipto_list: Type.Optional(Type.Array(Type.Integer())),
  business_types: Type.Optional(Type.Array(BannerBusinessType)),
});

const CreateBannerBody = Type.Intersect([
  Type.Omit(DbSchema['banner'], [
    'id',
    'created_at',
    'created_by',
    'updated_at',
    'updated_by',
    'deleted_at',
    'deleted_by',
    'filename',
  ]),
  OptionalOverrides,
  PersonalizedExtras,
]);

export const BannerCrudSchema = {
  path: '/banner',
  createBanner: {
    path: '',
    body: CreateBannerBody,
    response: DbSchema['banner'],
  },
  getManyBanner: {
    path: '',
    query: Type.Partial(Type.Pick(ReadManySchema, ['page', 'size'])),
    response: Type.Array(
      Type.Intersect([
        DbSchema['banner'],
        Type.Object({
          // existing field, now optional
          filename: Type.Optional(Nullable(Type.String())),

          // computed fields
          // filled only when banner_type = 'general', else null
          distribution_channel_count: Type.Optional(Nullable(Type.Number())),

          // count from banner_business_types (0+). Keep optional for flexibility.
          category_count: Type.Optional(Type.Number()),

          // filled only when banner_type = 'specific', else null
          shipto_count: Type.Optional(Nullable(Type.Number())),
        }),
      ])
    ),
  },
  getManyBannerPersonalization: {
    path: '/personalized',
    query: Type.Partial(Type.Pick(ReadManySchema, ['page', 'size'])),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          DbSchema['banner'],
          Type.Object({
            filename: Type.Optional(Nullable(Type.String())),
            distribution_channel_count: Type.Optional(Nullable(Type.Integer())),
            category_count: Type.Optional(Type.Integer()),
            shipto_count: Type.Optional(Nullable(Type.Integer())),
            business_types: Type.Optional(
              Type.Array(
                Type.Object({
                  cg_3_business_type: Type.String(),
                  cg_3_business_type_desc: Type.String(),
                })
            ))
          }),
        ])
      ),
      pagination: Type.Object({
        rows: Type.Integer({ minimum: 0 }),
        pages: Type.Integer({ minimum: 1 }),
      }),
    }),
  },
  buyerGetManyBanner: {
    path: '',
    query: OptionalWarehouseIdSchema,
    response: Type.Array(
      Type.Intersect([
        Type.Omit(DbSchema['banner'], [
          'distribution_channel',
          'business_segmentation',
        ]),
        Type.Object({
          filename: Type.String(),
        }),
      ])
    ),
  },
  reorderBanner: {
    path: '/:id/reorder',
    body: Type.Pick(DbSchema['banner'], ['index']),
    response: Type.Array(DbSchema['banner']),
  },
  updateBanner: {
    path: '/:id',
    body: Type.Intersect([
      Type.Object({
        title: Type.Optional(B.title),
        url: Type.Optional(B.url),
        image: Type.Optional(B.image),
        index: Type.Optional(B.index),
        business_segmentation: Type.Optional(B.business_segmentation),
        distribution_channel: Type.Optional(B.distribution_channel),
        promo_id: Type.Optional(B.promo_id),
        business_types: Type.Optional(Type.Array(BannerBusinessType)),
        shipto_csv_url: Type.Optional(B.shipto_csv_url)
      }),
    ]),
    response: DbSchema['banner'],
  },
  bringToTopBanner: {
    path: '/:id/bring-to-top',
    params: paramByIDSchema,
    response: Type.Null(),
  },
  deleteBanner: {
    path: '/:id',
    response: Type.Object({ message: Type.String() }),
  },
  uploadBanner: {
    path: '/upload',
    body: Type.Object({
      file: TypeCustom.Custom<SavedMultipartFile>(),
    }),
    response: Type.Object({
      url: Type.String(),
      filename: Type.String(),
    }),
  },
  uploadShiptoCsv: {
    path: '/upload-shipto-csv',
    body: Type.Object({
      file: TypeCustom.Custom<MultipartFile>(),
    }),
    response: Type.Object({
      url: Type.String(),
      stats: Type.Object({
        total: Type.Integer(),
        success_count: Type.Integer(),
        not_found_count: Type.Integer(),
        duplicates_ignored: Type.Integer(),
      }),
      message: Type.String(),
    }),
  },
};
export type BannerCrud = RecursiveStatic<typeof BannerCrudSchema>;
